import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { UserTable } from "@/features/users/components/UserTable";
import { UsersStats } from "@/features/users/components/UsersStats";
import { useUsers } from "@/features/users/hooks/useUsersQueries";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { useGovernanceAllowedDepartments } from "@/hooks/useDepartmentScope";
import { useDebounce } from "@/hooks/useDebounce";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { Button } from "@/components/ui/button";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import type { ListUsersQuery } from "@/types/user";

export default function UsersPage() {
  const { canCreate } = useUserPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = Math.max(1, Number(searchParams.get("page") ?? 1));
  const limitFromUrl = Math.max(1, Number(searchParams.get("limit") ?? 10));
  const roleFromUrl = searchParams.get("role") ?? undefined;
  const departmentFromUrl = searchParams.get("departmentId") ?? undefined;
  const isActiveFromUrl = searchParams.has("isActive")
    ? searchParams.get("isActive") === "true"
    : undefined;
  const searchFromUrl = searchParams.get("search") ?? "";

  const [searchInput, setSearchInput] = useState(searchFromUrl);
  const debouncedSearch = useDebounce(searchInput, 400);

  const { data: departmentsData } = useDepartments({ limit: 100 });
  const allDepartments = departmentsData?.items ?? [];
  const departments = useGovernanceAllowedDepartments(allDepartments).map((d) => ({
    id: d.id,
    nameAr: d.nameAr,
  }));

  useEffect(() => {
    setSearchInput(searchParams.get("search") ?? "");
  }, [searchParams]);

  const isMountRef = useRef(true);
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const filters: ListUsersQuery = {
    page: pageFromUrl,
    limit: limitFromUrl,
    ...(searchFromUrl && { search: searchFromUrl }),
    ...(roleFromUrl && { role: roleFromUrl }),
    ...(departmentFromUrl && { departmentId: departmentFromUrl }),
    ...(isActiveFromUrl !== undefined && { isActive: isActiveFromUrl }),
  };

  const { data, isLoading, error } = useUsers(filters);

  const patchParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value !== undefined) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleRoleChange = (role: string | undefined) => {
    patchParams({ role, page: "1" });
  };

  const handleDepartmentChange = (departmentId: string | undefined) => {
    patchParams({ departmentId, page: "1" });
  };

  const handleIsActiveChange = (isActive: boolean | undefined) => {
    patchParams({
      isActive: isActive !== undefined ? String(isActive) : undefined,
      page: "1",
    });
  };

  const handlePageChange = (page: number) => {
    patchParams({ page: String(page) });
  };

  const handleLimitChange = (limit: number) => {
    patchParams({ limit: String(limit), page: "1" });
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchParams(
      { page: "1", limit: String(limitFromUrl) },
      { replace: true },
    );
  };

  const hasActiveFilters =
    !!searchFromUrl ||
    !!roleFromUrl ||
    !!departmentFromUrl ||
    isActiveFromUrl !== undefined;

  const users = data?.items ?? [];
  const meta = data?.meta;

  return (
    <AdminPageShell
      title="جميع المستخدمين"
      description="عرض وبحث وفلترة حسابات المستخدمين في المنصة"
      isLoading={isLoading}
      error={error}
      actions={
        canCreate ? (
          <Button asChild>
            <Link to={appPath(Pages.USERS, "new")}>إضافة مستخدم</Link>
          </Button>
        ) : undefined
      }
    >
      <UsersStats users={users} meta={meta} />
      <UserTable
        users={users}
        total={meta?.total ?? 0}
        page={pageFromUrl}
        limit={limitFromUrl}
        totalPages={meta?.totalPages ?? 1}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        role={roleFromUrl}
        departmentId={departmentFromUrl}
        isActive={isActiveFromUrl}
        onRoleChange={handleRoleChange}
        onDepartmentChange={handleDepartmentChange}
        onIsActiveChange={handleIsActiveChange}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        showDepartmentFilter={departments.length > 1}
        departments={departments}
      />
    </AdminPageShell>
  );
}
